// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 7058a8d4-c7d5-46b2-8d1c-c082944ee728

// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

// ---------------------------------------------------------------------------
// Primordia — guided-walkthrough persistence
// ---------------------------------------------------------------------------
// One localStorage key, one small guarded object: { completedAt?, skippedAt? }.
// Every read is wrapped, matching this codebase's convention that every
// localStorage parse site in src/cmdb is guarded (see CLAUDE.md) — a corrupted
// or quota-evicted value degrades to "the tour has never been seen" rather
// than throwing and breaking boot.
// ---------------------------------------------------------------------------

import { getConfig } from './config';

const STORAGE_KEY = 'primordia.tour.v1';

function readState() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (e) {
    return {};
  }
}

function writeState(patch) {
  try {
    const next = { ...readState(), ...patch };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch (e) {
    // Private browsing / storage disabled / over quota — the choice simply
    // won't survive a reload, the same tradeoff ThemeProvider makes for its
    // own 'primordia.theme' key. Non-fatal either way.
  }
}

// Whether this browser has ever finished OR explicitly dismissed the tour.
// Deliberately does not distinguish the two here — both mean "don't auto-
// start again"; `resetTour()` is the only way back to a clean slate.
export function hasSeenTour() {
  const s = readState();
  return !!(s.completedAt || s.skippedAt);
}

export function markTourCompleted() {
  writeState({ completedAt: new Date().toISOString() });
}

export function markTourSkipped() {
  writeState({ skippedAt: new Date().toISOString() });
}

// Never auto-start for:
//   - an unauthenticated session — nothing to walk through yet; Login owns
//     that screen, and a tour racing the sign-in gate would spotlight
//     elements that are about to unmount;
//   - local dev running with authRequired:false, where "session" is often
//     absent or synthetic and a stranger's first `npm start` should not be
//     interrupted by a product tour aimed at a real new hire;
//   - a browser that has already completed OR explicitly skipped it once.
// Every branch is a silent `false`, never a thrown error — a walkthrough that
// can crash the boot sequence is worse than one that never appears.
export function shouldAutoStart(session) {
  try {
    if (!session) return false;
    if (!getConfig().authRequired) return false;
    return !hasSeenTour();
  } catch (e) {
    return false;
  }
}

// Backs the "Replay the tour" entry point (Help Center). Clears the persisted
// decision so a manual replay starts from the same state a first run would.
export function resetTour() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // Nothing to clear if storage was never available in the first place.
  }
}

// ---------------------------------------------------------------------------
// Launch logic
// ---------------------------------------------------------------------------
// HelpCenter has no idea how the host app mounts <Tour> — same situation
// HelpBubble is in with "Open in wiki" (see HelpBubble.js's openHelpTopic),
// and solved the same way: fire a CustomEvent instead of threading a callback
// prop through every caller. CmdbApp subscribes once via onTourReplayRequested
// and flips whatever local state renders <Tour open>.
const REPLAY_EVENT = 'primordia:start-tour';

export function requestTourReplay() {
  resetTour();
  try {
    window.dispatchEvent(new CustomEvent(REPLAY_EVENT));
  } catch (e) {
    // Non-browser env — nothing to notify.
  }
}

// Returns an unsubscribe function, ready to hand straight to a useEffect
// cleanup, matching the primordia:open-help listener CmdbApp already runs.
export function onTourReplayRequested(handler) {
  window.addEventListener(REPLAY_EVENT, handler);
  return () => window.removeEventListener(REPLAY_EVENT, handler);
}
